import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    ViewEncapsulation,
    input,
    viewChild,
    output,
    inject,
    effect
} from '@angular/core';

import { ApWindowService } from '@ap/cdk';

/**
 * @ngdoc component
 * @name ApPopoverComponent
 */
@Component({
    selector: 'ap-popover',
    templateUrl: './popover.component.html',
    styleUrls: ['./popover.component.scss'],
    encapsulation: ViewEncapsulation.Emulated
})
export class ApPopoverComponent implements AfterViewInit {
    private windowService = inject(ApWindowService);
    private readonly cdr = inject(ChangeDetectorRef);

    /**
     * Should the popover close when focus is outside own window
     */
    public readonly hideOnBlur = input<boolean>(undefined);

    /**
     * Position of the arrow on the popover; default = left.
     * (And alignment of the popover itself)
     */
    public readonly position = input<'left' | 'right' | 'auto'>('left');

    /**
     * Position of the content within the popover;
     * Default = left.
     */
    public readonly contentAlignment = input<'left' | 'right'>('left');

    /**
     * Emits boolean when open state changes
     */
    public readonly isOpenChanged = output<boolean>();

    protected id = `popover-${Math.floor(Math.random() * 9999)}`;

    protected readonly popoverParent = viewChild<ElementRef>('popoverParent');
    protected readonly innerContent = viewChild<ElementRef>('innerContent');

    protected isOpened = false;
    protected maxWidth: number;

    protected width = 'auto';

    private window: Window;

    protected calculatedPosition: 'left' | 'right' | 'auto';

    constructor() {
        this.window = this.windowService.getWindow();

        effect(() => {
            const parent = this.popoverParent();
            if (parent) {
                this.determineOffsetToCalculate();
            }
        });
    }

    public ngAfterViewInit(): void {
        this.determineOffsetToCalculate();
        this.cdr.detectChanges();
    }

    @HostListener('window:resize')
    onResize() {
        if (this.isOpened) {
            this.setWidth();
        }
    }

    @HostListener('focusout', ['$event'])
    public onFocusout(event: FocusEvent) {
        if (!this.isOpened) {
            return;
        }

        const relatedTarget = event.relatedTarget as Node;

        if (!this.innerContent().nativeElement.contains(relatedTarget)) {
            this.setOpenedState(false);
        }
    }

    /**
     * Close popover when user pressed Escape
     * @param event keyboardEvent Escape
     */
    @HostListener('keydown', ['$event'])
    public onEscapeKey(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.setOpenedState(false);
        }
    }

    onToggle() {
        this.setOpenedState(!this.isOpened);
    }

    private setOpenedState(isOpen: boolean) {
        this.isOpened = isOpen;
        this.isOpenChanged.emit(this.isOpened);
    }

    /**
     * Calculate which position
     */
    private calculatePosition(): void {
        const rect = this.popoverParent().nativeElement.getBoundingClientRect();
        const sizeLeft = rect.left;
        const sizeRight = this.window.innerWidth - rect.right;
        const popoverWidth = this.popoverParent().nativeElement.offsetWidth;

        if (this.position() === 'auto') {
            this.calculatedPosition = sizeRight >= popoverWidth || sizeRight > sizeLeft ? 'right' : 'left';
        } else {
            this.calculatedPosition = 'left';
        }
    }

    /**
     * Determine how to calculate offset based on position
     */
    private determineOffsetToCalculate(): void {
        this.calculatePosition();
        if (this.calculatedPosition === 'right') {
            this.setWidth();
        } else if (this.calculatedPosition === 'left') {
            this.setWidth();
        }
    }

    /**
     * Set max width of popover based on screen width
     */
    private setWidth(): void {
        const popoverDimensions = this.popoverParent().nativeElement.getBoundingClientRect();
        const windowWidth = this.window.innerWidth;

        const position = this.position();
        if (position === 'right') {
            this.maxWidth = windowWidth + popoverDimensions.x;
        } else if (position === 'left') {
            this.maxWidth = popoverDimensions.y;
        } else {
            this.maxWidth = windowWidth - (popoverDimensions.width / 2 + popoverDimensions.x);
        }

        this.maxWidth = Math.max(200, this.maxWidth);
    }
}
